import React, { FunctionComponent, useMemo, useState } from 'react';

import { css } from '@emotion/react';
import { ErrorMessage } from '@hookform/error-message';
import SearchIcon from '@mui/icons-material/Search';
import chunk from 'lodash/chunk';
import find from 'lodash/find';
import { SubmitHandler, useForm } from 'react-hook-form';

import Button from '@components/Common/Button';
import PaginationWithoutQuery from '@components/Common/PaginationWithoutQuery';
import Table from '@components/Common/Table';
import CommonModal from '@components/Modals/CommonModal';

import { useInnoforestCompany } from '@hooks/api/portfolio/usePortfolioAPIs';

import { palette } from '@styles/theme';

import { companySearchValidationOptions } from '@constants/validation';

/** 검색결과 테이블 Header */
const TABLE_HEADER: any[] = [
  {
    label: '선택',
    value: 'select',
    width: '100px',
  },
  {
    label: '기업ID',
    value: 'mappingId',
    width: '120px',
  },
  {
    label: '기업명',
    value: 'name',
    width: '180px',
  },
  {
    label: '서비스명',
    value: 'serviceComponents',
  },
  {
    label: '대표자명',
    value: 'ceo',
    width: '160px',
  },
];

/** 검색어 입력 form type */
interface FormInputs {
  name: string;
}

/** 메인 컴포넌트 type */
interface Props {
  open: boolean;
  handleOpen: () => void;
  handleSelect: any;
}

/** 페이지 사이즈 */
const PAGE_SIZE = 5;

/** 메인 컴포넌트 */
const CompanySearchModal: FunctionComponent<Props> = ({
  open,
  handleOpen,
  handleSelect,
}) => {
  /** 검색 Input onFocus state */
  const [focus, setFocus] = useState<boolean>(false);

  /** Pagination */
  const [pageNo, setPageNo] = useState<number>(1);

  /** react-hook-form Init */
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<FormInputs>();

  /** 기업검색 handler */
  const onSumbit: SubmitHandler<FormInputs> = async ({ name }) => {
    try {
      mutate({
        name: name,
        page: 1,
        size: PAGE_SIZE,
      });
      setPageNo(1);
    } catch (error: any) {
      console.log('SEARCH_ERROR');
    }
  };

  /** 결과리스트에서 선택 버튼을 눌렀을 경우 */
  const handleClick = (mappingId: string) => {
    handleSelect(find(searchData, { mappingId: mappingId }));
    handleOpen();
  };

  /** 검색결과 API 호출 */
  const { mutate, data, totalCount } = useInnoforestCompany();

  /** 검색결과 데이터 정리 */
  const searchData = useMemo(
    () =>
      data?.map((d: any) => ({
        ...d,
        id: d?.mappingId,
        serviceComponents: <div css={textHide}>{d?.serviceName || '-'}</div>,
        select: (
          <div css={selectButton} onClick={() => handleClick(d?.mappingId)}>
            <Button label="선택" fontSize={12} height={30} />
          </div>
        ),
      })),
    [data]
  );

  return (
    <CommonModal
      open={open}
      handleOpen={handleOpen}
      title="기업검색"
      width={900}
      // height={650}
    >
      <div css={{ display: 'flex', flexDirection: 'column' }}>
        <form onSubmit={handleSubmit(onSumbit)}>
          <div
            css={[inputWrap, focus && inputWrapOnFocus]}
            onFocus={() => setFocus(true)}
            onBlur={() => setFocus(false)}
          >
            <input
              css={inputs}
              type="text"
              autoComplete="false"
              placeholder="기업명을 입력해주세요."
              {...register('name', companySearchValidationOptions)}
            />
            <SearchIcon />
          </div>
          <ErrorMessage
            errors={errors}
            name="name"
            render={({ message }) => <p css={errorMessage}>{message}</p>}
          />
        </form>

        {searchData && (
          <>
            <div css={result}>검색결과: {totalCount}</div>
            <div css={resultList}>
              <Table
                header={TABLE_HEADER}
                data={chunk(searchData, PAGE_SIZE)[pageNo - 1] || []}
              />
              <PaginationWithoutQuery
                pageNo={pageNo}
                setPageNo={setPageNo}
                pageSize={PAGE_SIZE}
                totalCount={totalCount}
              />
            </div>
          </>
        )}
      </div>
    </CommonModal>
  );
};

export default CompanySearchModal;

const inputWrap = css`
  display: flex;
  align-items: center;
  width: 260px;
  padding-right: 10px;
  border-radius: 5px;
  border: 1px solid ${palette.gray3};
`;

const inputWrapOnFocus = css`
  border-color: ${palette.primaryColor};
`;

const inputs = css`
  width: 100%;
  height: 36px;
  border-radius: 5px;
  padding: 0 10px;
  background-color: ${palette.white};
  display: flex;

  :focus {
    outline: none;
  }
`;

const selectButton = css`
  display: flex;
  align-items: center;
  justify-content: center;
  button {
    background-color: ${palette.secondaryColor};
    &:hover,
    &:active {
      background-color: ${palette.secondaryColor};
      opacity: 0.8;
    }
  }
`;

const result = css`
  font-size: 14px;
  font-weight: 500;
  margin: 20px 0 10px 0;
`;

const errorMessage = css`
  color: ${palette.colorDanger};
  font-size: 12px;
  padding: 3px 5px;
  height: 22px;
`;

const resultList = css`
  display: flex;
  width: 840px;
  // height: 280px;
  flex-direction: column;
  justify-content: space-between;
`;

const textHide = css`
  width: 100%;
  display: -webkit-box;
  word-wrap: break-word;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  overflow: hidden;
`;
